import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFamiliesWithUsage, useDeleteFamily } from '@/api/familyApi';
import { Family } from '@/types/family';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  SearchIcon,
} from 'lucide-react';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import ErrorBoundary from '@/components/ErrorBoundary';
import { logError } from '@/utils/errorHandler';

interface FamilyListPageProps {
  isEmbedded?: boolean;
}

export function FamilyListPage({ isEmbedded = false }: FamilyListPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);

  // Get organization settings for date formatting
  const { orgSettings } = useOrgSettings();
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  const { data: families, isLoading, isError, error } = useFamiliesWithUsage();
  const deleteFamily = useDeleteFamily();

  // Use configuration for pagination
  const PAGE_SIZE = 10; // Default page size

  // Filter families based on search term
  const filteredFamilies = useMemo(() => {
    if (!families) return [];

    if (!search) return families;

    const searchLower = search.toLowerCase();
    return families.filter(
      family =>
        family.code.toLowerCase().includes(searchLower) ||
        family.label.toLowerCase().includes(searchLower)
    );
  }, [families, search]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredFamilies.length / PAGE_SIZE);
  const paginatedFamilies = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredFamilies.slice(start, end);
  }, [filteredFamilies, page, PAGE_SIZE]);

  // Handle delete
  const handleDelete = async () => {
    if (!selectedFamily) return;

    try {
      await deleteFamily.mutateAsync(selectedFamily.id);
      setSelectedFamily(null);
    } catch (err) {
      logError(err as Error, {
        component: 'FamilyListPage',
        action: 'deleteFamily',
        metadata: { familyId: selectedFamily.id, familyCode: selectedFamily.code },
      });
    }
  };

  // Reset page when search changes
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Navigate to previous/next page
  const goToPrevPage = () => setPage(p => Math.max(1, p - 1));
  const goToNextPage = () => setPage(p => Math.min(totalPages, p + 1));

  if (isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-6">
        <h2 className="mb-2 text-lg font-medium text-red-800">{t('families.list.loadingError')}</h2>
        <p className="text-red-700">
          {error instanceof Error ? error.message : t('families.list.unknownError')}
        </p>
      </div>
    );
  }

  const content = (
    <>
      <div className="flex items-center justify-between">
        {!isEmbedded && <h1 className="text-2xl font-bold">{t('families.list.title')}</h1>}
        <Button
          onClick={() => navigate('/app/products/families/new')}
          className="bg-teal-600 text-white hover:bg-teal-700"
        >
          <PlusIcon className="mr-2 size-4" />
          {t('families.actions.create')}
        </Button>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="relative w-64">
          <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-enterprise-400" />
          <Input
            placeholder={t('families.list.searchPlaceholder')}
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {!isLoading && (
          <div className="text-sm text-enterprise-500">
            {totalPages > 0
              ? `${t('families.table.pagination.showing')} ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, filteredFamilies.length)} ${t('families.table.pagination.of')} ${filteredFamilies.length}`
              : t('families.table.emptyState.noResults')}
          </div>
        )}
      </div>

      <div className={isEmbedded ? '' : 'mt-4 rounded-md border'}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('families.table.headers.code')}</TableHead>
              <TableHead>{t('families.table.headers.label')}</TableHead>
              <TableHead>{t('families.table.headers.attributeGroups')}</TableHead>
              <TableHead>{t('families.table.headers.usage')}</TableHead>
              <TableHead>{t('families.table.headers.createdAt')}</TableHead>
              <TableHead className="text-right">{t('families.table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-5 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-36" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-24" />
                  </TableCell>
                </TableRow>
              ))
            ) : paginatedFamilies.length > 0 ? (
              paginatedFamilies.map(family => (
                <TableRow key={family.id}>
                  <TableCell className="font-medium">{family.code}</TableCell>
                  <TableCell>{family.label}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{family.attribute_groups?.length || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {family.usage ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {family.usage.productCount} {t('families.usage.products')}
                            </Badge>
                            {family.usage.hasValues && (
                              <Badge
                                variant="secondary"
                                className="bg-green-50 text-xs text-green-700"
                              >
                                {t('families.usage.hasValues')}
                              </Badge>
                            )}
                          </div>
                          {family.usage.attributeGroupCount !==
                            (family.attribute_groups?.length || 0) && (
                            <Badge variant="outline" className="text-xs text-orange-600">
                              {family.usage.attributeGroupCount} {t('families.usage.activeGroups')}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {t('families.usage.noUsageData')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {family.created_at ? formatDateForDisplay(family.created_at, dateFormat) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/products/families/${family.id}`)}
                      >
                        <EyeIcon className="size-4" />
                        <span className="sr-only">{t('families.actions.view')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/products/families/${family.id}/edit`)}
                      >
                        <PencilIcon className="size-4" />
                        <span className="sr-only">{t('families.actions.edit')}</span>
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500"
                            onClick={() => setSelectedFamily(family)}
                          >
                            <TrashIcon className="size-4" />
                            <span className="sr-only">{t('families.actions.delete')}</span>
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('families.actions.delete')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('families.messages.deleteConfirm', {
                                code: family.code || '',
                              })}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('families.messages.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-500 hover:bg-red-600"
                              onClick={handleDelete}
                            >
                              {t('families.actions.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-enterprise-400">
                  {search
                    ? t('families.table.emptyState.noResults')
                    : t('families.table.emptyState.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center space-x-2">
          <Button variant="outline" size="sm" onClick={goToPrevPage} disabled={page === 1}>
            <ChevronLeftIcon className="size-4" />
            <span className="ml-1">{t('families.table.pagination.previous')}</span>
          </Button>
          <div className="text-sm text-enterprise-500">
            {t('families.table.pagination.page')} {page} {t('families.table.pagination.of')}{' '}
            {totalPages}
          </div>
          <Button variant="outline" size="sm" onClick={goToNextPage} disabled={page === totalPages}>
            <span className="mr-1">{t('families.table.pagination.next')}</span>
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      )}
    </>
  );

  // If embedded, return content directly
  if (isEmbedded) {
    return content;
  }

  // Otherwise wrap with the standard container
  return (
    <ErrorBoundary>
      <div className="space-y-6 py-4">{content}</div>
    </ErrorBoundary>
  );
}
